import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { GoogleIcon, FacebookIcon, AppleIcon } from './SocialIcons';
import { useAuth } from "../contexts/AuthContext.jsx";
import { useLanguage } from "../contexts/LanguageContext.jsx";
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loading: authLoading, loginWithGoogle, loginWithFacebook, loginWithApple } = useAuth();
  const { language, setLanguage, t } = useLanguage();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!identifier || !password) {
      setError('Vui lòng nhập tên đăng nhập và mật khẩu');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Gọi API login thực tế thông qua AuthContext
      const result = await login(identifier, password);

      if (result.success) {
        // Lấy trang đích từ state hoặc mặc định là dashboard
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Đăng nhập thất bại');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Đã xảy ra lỗi khi đăng nhập. Vui lòng thử lại.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setIsSubmitting(true);
    setError('');

    let result;
    if (provider === 'Google') {
      result = await loginWithGoogle();
    } else if (provider === 'Facebook') {
      result = await loginWithFacebook();
    } else if (provider === 'Apple') {
      result = await loginWithApple();
    }

    if (result && result.success) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } else {
      setError(result?.error || `Login with ${provider} failed`);
      setIsSubmitting(false);
    }
  };

  // Hiển thị loading nếu AuthContext đang loading
  if (authLoading) {
    return (
      <div className="login-container">
        <div className="login-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
          <div className="loading-spinner" style={{ width: '44px', height: '44px', minWidth: '44px', minHeight: '44px', margin: '0 auto' }}></div>
          <p style={{ marginTop: '20px', color: '#4a5568', fontWeight: 'bold' }}>{t('login.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="lang-switch-container" style={{ position: 'absolute', top: '20px', right: '20px' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          background: 'rgba(255, 255, 255, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          borderRadius: '9999px',
          padding: '6px 20px',
          backdropFilter: 'blur(10px)',
          color: 'white',
          fontWeight: '700',
          fontSize: '1rem',
          letterSpacing: '1px'
        }}>
          <span 
            onClick={() => setLanguage('vi')}
            style={{ 
              cursor: 'pointer', 
              opacity: language === 'vi' ? 1 : 0.5,
              borderBottom: language === 'vi' ? '2px solid white' : '2px solid transparent',
              paddingBottom: '2px',
              transition: 'all 0.2s'
            }}>VN</span>
          <span style={{ opacity: 0.6, fontSize: '0.9rem' }}>/</span>
          <span 
            onClick={() => setLanguage('en')}
            style={{ 
              cursor: 'pointer', 
              opacity: language === 'en' ? 1 : 0.5,
              borderBottom: language === 'en' ? '2px solid white' : '2px solid transparent',
              paddingBottom: '2px',
              transition: 'all 0.2s'
            }}>EN</span>
        </div>
      </div>

      <div className="login-card">
        {/* Logo/Title */}
        <div className="logo-container">
          <h1 className="logo-text">{t('login.title')}</h1>
        </div>

        {/* Hiển thị lỗi */}
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* Form đăng nhập */}
        <form className="login-form" onSubmit={handleSubmit}>
          {/* Username/Email Field */}
          <div className="form-group">
            <label className="form-label">{t('login.username')}</label>
            <input
              type="text"
              className="form-input"
              placeholder={t('login.placeholderUsername') || "Tên hoặc email"}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              disabled={isSubmitting || authLoading}
              autoComplete="username"
              autoCapitalize="none"
            />
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label className="form-label">{t('login.password')}</label>
            <input
              type="password"
              className="form-input"
              placeholder={t('login.placeholderPassword')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isSubmitting || authLoading}
              autoComplete="current-password"
            />
          </div>

          {/* Buttons */}
          <div className="login-buttons">
            <button
              type="submit"
              className="login-button"
              disabled={isSubmitting || authLoading}
            >
              {isSubmitting ? (
                <>
                  <span className="button-spinner"></span>
                  {t('login.loading')}
                </>
              ) : t('login.loginButton')}
            </button>
            <button
              type="button"
              className="register-button primary"
              onClick={() => navigate('/register')}
              disabled={isSubmitting || authLoading}
            >
              {t('login.registerButton')}
            </button>
          </div>
        </form>

        {/* Divider với text "Or login with" */}
        <div className="divider-container">
          <div className="divider-line"></div>
          <span className="divider-text">{t('login.divider')}</span>
          <div className="divider-line"></div>
        </div>

        {/* Social Login Buttons tương tự Register.css style */}
        <div className="social-buttons-container">
          <button
            className="social-button google"
            onClick={() => handleSocialLogin('Google')}
            title="Google"
          >
            <GoogleIcon />
          </button>
          <button
            className="social-button facebook"
            onClick={() => handleSocialLogin('Facebook')}
            title="Facebook"
          >
            <FacebookIcon />
          </button>
          <button
            className="social-button apple"
            onClick={() => handleSocialLogin('Apple')}
            title="Apple"
          >
            <AppleIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;